/**
 * battle(전투 그리드) 도메인 설치자 (S6 전반) — BattleTabView + 잠금 해제 확인 팝업 +
 * api{grid 7종} 기여. 그리드 커맨드는 셸 생성분(boot.commands.grid — AutoPlay 동결 thin과 공유).
 *
 * 전장 실행(BattlefieldRunner)·조준선·게임뷰 프레임/마스크는 물리/프레임 루프 결합이라 셸 잔존
 * (패러다임 규칙 1 — Phaser 실행 셸 범위 명시). 여기는 탭 뷰·모달 조립·api만.
 * battle 탭은 resident(상주) — flushed 배치 갱신·전역 드래그 설정이 상시 의존.
 */
import { unlockCostLabel } from "../ui/formatNumber.js";
import { BattleTabView, type BattleActiveSynergy } from "../ui/BattleTabView.js";
import { MODULE_GRID } from "../data/balance.js";
import { findPath, type PathResult } from "../modules/ModulePathFinder.js";
import { buildPaletteRowsCached, createPaletteRowsCache } from "../ui/tabData.js";
import { effectiveRarity } from "../models/ModuleInstance.js";
import { resolveModuleSynergies } from "../systems/ModuleSynergyResolver.js";
import { resolveSynergyContributors } from "../systems/synergyContributors.js";
import type { ModuleSlotType } from "../models/ModuleTypes.js";
import type { ModuleRarity } from "../modules/ProjectileModule.js";
import type { GameContext } from "../app/GameContext.js";
import { openModuleInfoModal, openLauncherInfoModal } from "../ui/moduleInfoModal.js";
import { openCodexModuleInfo } from "../ui/codexInfoModal.js";
import { openCodexSynergyInfo } from "../ui/codexSynergyInfoModal.js";
import { REWARD_ICON_KEY } from "../ui/rewardIcons.js";
import { portsFor } from "../ui/shapeRenderer.js";
import { cellPortsForPlaced } from "../data/modulePorts.js";
import { footprintCells, isSingleFootprint } from "../data/moduleFootprints.js";
import { SYNERGY_CODEX_UNLOCK_CHAPTER } from "../data/synergyUnlock.js";
import { t } from "../i18n/i18n.js";
import { spaceshipById } from "../data/spaceshipData.js";
import {
  dialogueIdsForTrigger,
  dialogueLinesById,
  type DialogueTrigger,
  type DialogueLine,
} from "../data/dialogueData.js";
import { WORLD_CHAPTER_SPAN } from "../data/worldData.js";
import type { BattleDialogueData } from "../ui/battleDialogueTypes.js";
import type { Direction } from "../models/ModuleGrid.js";
import type { DomainBootstrapper, GameBoot } from "./BootTypes.js";

export const battleBoot: DomainBootstrapper = {
  key: "battle",
  install(boot) {
    const { ctx } = boot;
    const grid = boot.commands.grid;
    const infoDeps = {
      modals: boot.modals,
      ctx,
      screen: () => boot.screen(),
      // 파이프 상세(정보 팝업)에서 강화 — moduleId 레벨업(강화석 전용). 명세: 그리드 카드 직접 강화 회피.
      upgradeModule: (moduleId: string) => grid.upgradeModule(moduleId),
      // 배치(장착)된 파이프 클릭 시 정보 팝업에 "장착 해제" 버튼 — (x,y) 점유 모듈을 인벤토리로 회수.
      // GridCommands.removeAt가 invalidateUi+persist 내장 → 해제 즉시 그리드/팔레트 갱신·저장.
      removeModuleAt: (x: number, y: number) => grid.removeAt(x, y),
    };
    const codexInfoDeps = {
      modals: boot.modals,
      ctx,
      screen: () => boot.screen(),
      discovered: () => boot.state.discovered(),
      upgradeModule: (moduleId: string) => grid.upgradeModule(moduleId),
    };

    // 패널 inset(frameInsetX) 산술은 battleTabLayout SSoT(computeBattleTabLayoutForScreen)가 소유 — 화면 폭만 전달.
    // 튜토리얼 인터랙션 게이트 — 안내받지 않은 그리드 오클릭(잠금셀 등)으로 엉뚱한 팝업이 뜨는 것을 차단.
    // 뷰 콜백 경로에만 적용(AutoPlay api 경로는 우회 유지). 미허용 시 조용히 무시.
    const paletteRowsCache = createPaletteRowsCache();
    let activeSynergyContributorsById: ReadonlyMap<string, ReadonlySet<string>> = new Map();
    let openBattleSynergyInfoId: string | null = null;
    const view = new BattleTabView(boot.scene, boot.screen().width, boot.layout.shell, boot.layout.tabContent, {
      onPlace: (x, y, instanceId) => {
        if (!boot.tutorialGate("module.place")) return false;
        return grid.place(x, y, instanceId);
      },
      onMove: (fx, fy, tx, ty) => {
        if (!boot.tutorialGate("module.move")) return;
        grid.move(fx, fy, tx, ty);
      },
      onModuleInfo: (id) => {
        if (!boot.tutorialGate("module.info")) return;
        openModuleInfoModal(infoDeps, id);
      },
      onLauncherInfo: () => {
        if (!boot.tutorialGate("launcher.info")) return;
        openLauncherInfoModal(infoDeps, spaceshipById(ctx.spaceship().selectedId));
      },
      onRemove: (x, y) => {
        if (!boot.tutorialGate("module.remove")) return;
        grid.removeAt(x, y);
      },
      onUnequipAll: () => grid.unequipAll(),
      onRecommendEquip: () => grid.recommendEquip(),
      onSynergyInfo: (synergyId) => {
        openBattleSynergyInfoId = synergyId;
        openCodexSynergyInfo(
          {
            modals: boot.modals,
            screen: () => boot.screen(),
            levelOf: (id) => ctx.synergyLevels().level(id),
            openModuleInfo: (definitionId) => openCodexModuleInfo(codexInfoDeps, definitionId),
            onHighlightPipes: (id) => {
              const contributors = activeSynergyContributorsById.get(id);
              if (contributors) view.highlightSynergyPipes(contributors);
            },
            onClose: () => {
              openBattleSynergyInfoId = null;
              view.clearFocusedSynergyPipes();
            },
          },
          synergyId,
        );
      },
      onUnlock: (x, y, visibleLaneIndex) => {
        if (!boot.tutorialGate("grid.unlock")) return;
        openUnlockConfirm(boot, x, y, visibleLaneIndex);
      },
      unlockCostBig: () => ctx.grid().unlockCostBig(),
      nameOf: (definitionId) => t(`module.${definitionId}.name`),
    });

    // 상주 탭(resident) — flushed 무조건 갱신 + scene 전역 드래그 설정 의존 (lazy 전환 금지).
    boot.tabs.registerLazy("battle", {
      resident: true,
      mount: () => ({
        update: () => {
          const grid = ctx.grid();
          const path = findPath(grid);
          const synergies = activeSynergyState(path, ctx);
          const spaceship = spaceshipById(ctx.spaceship().selectedId);
          activeSynergyContributorsById = synergies.contributorsBySynergyId;
          if (
            openBattleSynergyInfoId &&
            !synergies.activeSynergies.some((s) => s.id === openBattleSynergyInfoId)
          ) {
            boot.modals.closeSynergyInfo();
            openBattleSynergyInfoId = null;
            view.clearFocusedSynergyPipes();
          }
          view.updateAll(
            grid,
            path,
            buildPaletteRowsCached(paletteRowsCache, ctx.inventory(), grid, ctx.moduleRegistry(), (id) =>
              ctx.playerModuleLevels().level(id),
              ctx.playerModuleLevels().revision,
            ),
            boot.combatSpecSummary(),
            synergies.contributorIds,
            rarityByPlacedInstance(ctx),
            synergies.activeSynergies,
            spaceship?.largeAssetKey ?? "spaceship_large",
          );
          boot.battleDialogue.update(battleDialogueData(boot));
        },
        setVisible: (v) => {
          view.setVisible(v);
          boot.battleDialogue.setVisible(v);
        },
        applyLanguage: () => {
          view.applyLanguage();
          boot.battleDialogue.applyLanguage();
        },
        // 셸 펼치기/접기 — 그리드/팔레트를 늘어난(또는 원복된) 콘텐츠 rect 상단 기준으로 재배치(수동 핸들이라 명시 연결 필수).
        resize: (rect) => view.resize(rect),
      }),
    });
    boot.provideBattleProc((ids, startFired) => view.showProc(ids, startFired));
    boot.api.register({
      grantModule: (id, fp, rarity) => grid.grantModule(id, fp, rarity),
      placeModule: (x, y, id) => grid.place(x, y, id),
      moveModule: (fx, fy, tx, ty) => grid.move(fx, fy, tx, ty),
      removeModuleAt: (x, y) => grid.removeAt(x, y),
      unequipAllModules: () => grid.unequipAll(),
      recommendEquipModules: () => grid.recommendEquip(),
      unlockCell: (x, y) => openUnlockConfirm(boot, x, y),
      upgradeModule: (moduleId) => grid.upgradeModule(moduleId),
      openModuleInfo: (id, action) => openModuleInfoModal(infoDeps, id, action),
    });
  },
};

function battleDialogueData(boot: GameBoot): BattleDialogueData | null {
  const seen = new Set(boot.state.dialogueSeenIds());
  for (const trigger of battleDialogueTriggers(boot)) {
    for (const dialogueId of dialogueIdsForTrigger(trigger, "battle", undefined, "button")) {
      const lines = dialogueLinesById(dialogueId);
      if (lines.length === 0) continue;
      if (isOnceDialogue(lines) && seen.has(dialogueId)) continue;
      return {
        dialogueId,
        unread: !seen.has(dialogueId),
        lines: lines.map((line) => ({
          speaker: t(line.speakerKey),
          text: t(line.textKey),
        })),
      };
    }
  }
  return null;
}

function battleDialogueTriggers(boot: GameBoot): readonly DialogueTrigger[] {
  const triggers: DialogueTrigger[] = ["battleStart"];
  if (boot.ctx.rebirth().rebirthCount > 0) triggers.push("firstRebirth");
  if (boot.ctx.chapter().currentChapter === WORLD_CHAPTER_SPAN) triggers.push("w1BossEncounter");
  return triggers;
}

function isOnceDialogue(lines: readonly DialogueLine[]): boolean {
  return lines.some((line) => line.once);
}

/**
 * 잠금 칸 클릭 — 즉시 해제 대신 확인 팝업(오클릭 다이아 소모 방지). 미니맵으로 대상 셀 표시,
 * Yes(비용)/No — Yes에서만 GridCommands가 차감·해금. 다이아 부족 시 Yes는 canAfford 게이트로 흔들림 피드백.
 */
function openUnlockConfirm(boot: GameBoot, x: number, y: number, visibleLaneIndex = 0): void {
  const { ctx, modals } = boot;
  if (!ctx.grid().canUnlock(x, y)) return;
  const costBig = ctx.grid().unlockCostBig();
  const screen = boot.screen();
  modals.openGridUnlockConfirm({
    screenWidth: screen.width,
    screenHeight: screen.height,
    gridWidth: ctx.grid().width,
    gridHeight: ctx.grid().height,
    targetX: x,
    targetY: y,
    start: ctx.grid().start,
    fire: ctx.grid().fire,
    lanes: ctx.grid().lanes,
    visibleLaneIndex,
    maxVisibleRows: ctx.grid().lanes.length > 1 ? MODULE_GRID.visibleRows : undefined,
    costText: unlockCostLabel(costBig),
    costIconKey: REWARD_ICON_KEY.diamond,
    isUnlocked: (cellX, cellY) => ctx.grid().isUnlocked(cellX, cellY),
    cellPorts: (cellX, cellY) => miniGridCellPorts(boot, cellX, cellY),
    canAfford: () => ctx.economy().diamond.gte(costBig),
    onConfirm: () => {
      boot.commands.grid.unlockCell(x, y);
      modals.closeGridUnlockConfirm();
    },
    onCancel: () => modals.closeGridUnlockConfirm(),
  });
}

function rarityByPlacedInstance(ctx: GameContext): ReadonlyMap<string, ModuleRarity> {
  const out = new Map<string, ModuleRarity>();
  const inventory = ctx.inventory();
  const registry = ctx.moduleRegistry();
  for (const tile of ctx.grid().placed()) {
    const inst = inventory.getInstance(tile.instanceId);
    if (!inst) continue;
    out.set(tile.instanceId, effectiveRarity(inst, registry.getDefinition(inst.definitionId)));
  }
  return out;
}

/**
 * 활성 경로에서 발동 시너지와 기여 모듈을 함께 계산한다.
 * 기여 instanceId 집합은 ModuleGridView가 "synergy"(보라)로 강조하고,
 * activeSynergies는 BattleTabView의 전투탭 배지에 표시한다.
 * 활성 모듈을 definitionIds(fused면 컴포넌트 펼침)로 풀어 resolveModuleSynergies의 sourceModuleIds 기준과 정합시킨다
 * (CombatSystem.buildCombatSpec과 동일 입력 → 그리드 표시 시너지 = 실제 발동 시너지). 전투 외 편집 중에도 매 갱신 계산.
 */
function activeSynergyState(
  path: PathResult,
  ctx: GameContext,
): {
  readonly contributorIds: ReadonlySet<string>;
  readonly contributorsBySynergyId: ReadonlyMap<string, ReadonlySet<string>>;
  readonly activeSynergies: readonly BattleActiveSynergy[];
} {
  if (ctx.chapter().bestChapter < SYNERGY_CODEX_UNLOCK_CHAPTER) {
    return {
      contributorIds: new Set(),
      contributorsBySynergyId: new Map(),
      activeSynergies: [],
    };
  }
  const registry = ctx.moduleRegistry();
  const inventory = ctx.inventory();
  const activeModules = path.activeInstanceIds.map((iid) => {
    const inst = inventory.getInstance(iid);
    const tile = ctx.grid().placed().find((placed) => placed.instanceId === iid);
    const definitionIds = inst?.fused
      ? inst.fused.map((c) => c.definitionId)
      : inst
        ? [inst.definitionId]
        : [];
    const cells = tile
      ? footprintCells(tile.footprintId).map((c) => ({ x: tile.x + c.dx, y: tile.y + c.dy }))
      : [];
    return { instanceId: iid, definitionIds, cells };
  });
  const activeModuleIds = activeModules.flatMap((m) => m.definitionIds);
  const activeModuleGroups = activeModules.map((m) => m.definitionIds);
  const activeModuleSlots = activeModules.map((m) => ({ definitionIds: m.definitionIds, cells: m.cells }));
  const activeSlotTypes = activeModuleIds
    .map((id) => registry.slotOf(id))
    .filter((s): s is ModuleSlotType => !!s);
  const { matched } = resolveModuleSynergies({
    activeModuleIds,
    activeSlotTypes,
    activeModuleGroups,
    activeModuleSlots,
  });
  const contributorsBySynergyId = new Map<string, ReadonlySet<string>>();
  for (const def of matched) {
    contributorsBySynergyId.set(def.id, resolveSynergyContributors([def], activeModules));
  }
  return {
    contributorIds: resolveSynergyContributors(matched, activeModules),
    contributorsBySynergyId,
    activeSynergies: matched.map((def) => ({
      id: def.id,
      name: t(`synergy.${def.id}.name`),
      label: def.requiredModuleIds.map((id) => t(`module.${id}.name`)).join("+"),
      grade: def.grade,
      level: ctx.synergyLevels().level(def.id),
    })),
  };
}

/** 미니맵용 — 셀의 설치 모듈 포트 방향(없으면 null). single/multi footprint 모두 그 셀 포트만. */
function miniGridCellPorts(boot: GameBoot, x: number, y: number): Direction[] | null {
  const cover = boot.ctx.grid().moduleCoveringCell(x, y);
  if (!cover) return null;
  return isSingleFootprint(cover.tile.footprintId)
    ? portsFor(cover.tile.shapeId)
    : (cellPortsForPlaced(cover.tile.shapeId, cover.tile.footprintId)[cover.cellIndex] ?? []);
}
